import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import {
    notificationsService,
    type ApiNotification,
} from '../services/notificationsService';
import toast from 'react-hot-toast';

interface NotificationsContextType {
    notifications: ApiNotification[];
    unreadCount: number;
    loading: boolean;
    loadNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotifications: (ids: string[]) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
    const [notifications, setNotifications] = useState<ApiNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const isPausedRef = useRef(false);

    // Función auxiliar para determinar si es proveedor dinámicamente
    const checkIsSupplier = () => window.location.pathname.startsWith('/proveedores');

    const fetchInternal = async () => {
        const path = window.location.pathname;
        if (path.includes('/login') || isPausedRef.current) {
            setLoading(false);
            return;
        }

        const isSup = checkIsSupplier();
        const token = isSup ? localStorage.getItem('supplier_access_token') : localStorage.getItem('access_token');

        if (!token) {
            setLoading(false);
            return;
        }

        try {
            // Se piden todas y el filtro por módulo queda en la pantalla.
            //
            // Antes se filtraba aquí también, por la ruta, y eran dos filtros
            // distintos decidiendo lo mismo: en `/dashboard` este pedía las de
            // RH, así que las de Almacén no llegaban al navegador y la
            // campanita no podía mostrarlas aunque quisiera. Este proveedor
            // vive por fuera del Router y no ve los cambios de navegación, así
            // que era el peor lugar para decidirlo.
            const list = await notificationsService.getAll(isSup);
            const safeList = Array.isArray(list) ? list : [];
            safeList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

            if (!isPausedRef.current) setNotifications(safeList);
        } catch (errorCrudo: unknown) {
          const error = errorCrudo as {
            message?: string;
            statusCode?: number;
            status?: number;
            error?: string;
            response?: { status?: number; data?: { message?: string } };
          };
            if (error?.response?.status === 401) isPausedRef.current = true;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInternal();
        const interval = setInterval(fetchInternal, 60000);
        return () => clearInterval(interval);
    }, []);

    const markAsRead = async (id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
        try {
            await notificationsService.markAsRead(id, checkIsSupplier());
        } catch {
            fetchInternal();
        }
    };

    const markAllAsRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        try {
            await notificationsService.markAllAsRead(checkIsSupplier());
            toast.success('Todas leídas');
        } catch {
            fetchInternal();
        }
    };

    const deleteNotifications = async (ids: string[]) => {
        const prev = [...notifications];
        setNotifications(prev.filter(n => !ids.includes(n.id)));
        try {
            await notificationsService.delete(ids, checkIsSupplier());
            toast.success('Eliminadas');
        } catch {
            setNotifications(prev);
            toast.error('Error al eliminar');
        }
    };

    return (
        <NotificationsContext.Provider value={{
            notifications,
            unreadCount: notifications.filter(n => !n.isRead).length,
            loading,
            loadNotifications: fetchInternal,
            markAsRead,
            markAllAsRead,
            deleteNotifications
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) throw new Error('useNotifications debe usarse dentro de un NotificationsProvider');
    return context;
};